import React, { useState, useEffect } from 'react';
import { useSocket } from '../utils/SocketContext';
import { format, formatDistanceToNow } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDeviceNotifications, fetchDeviceAlerts } from '../features/dashboardSlice';
import { fetchDeviceAnalysis, fetchDeviceChartData } from '../features/authSlice';
import BarChart from './AreaLineChart';
import LineChart from './LineChart';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WaterLevelGauge from './WaterLevelGauge';
import { useParams, useLocation } from 'react-router-dom';
import '../styles/deviceAnalysisPage.css';
import waterLevel25 from '../assets/images/water_level_25.svg';
import waterLevel50 from '../assets/images/water_level_50.svg';
import waterLevel75 from '../assets/images/water_level_75.svg';
import waterLevel100 from '../assets/images/water_level_100.svg';
import phoneIcon from '../assets/images/phone_icon.svg';
import emailIcon from '../assets/images/email_icon.svg';
import locationIcon from '../assets/images/location_round.svg';
import calendarIcon from '../assets/images/calendar.svg';
import settingIcon from '../assets/images/setting.svg';
import { SOLAR_BATTERY_LEVEL_RANGES, WATER_TEMPERATURE_RANGES, TDS_RANGES, WATER_TANK_LEVEL_RANGES } from '../constants/constant';
import { getTimeAgo } from '../utils/dateUtils';
import DeviceNotificationSettingsModal from './DeviceNotificationSettingsModal';
import alertTriangleIcon from '../assets/images/alert_triangle_icon.svg';
import { hexToCssFilter } from '../utils/hexToCssFilter';


const DeviceAnalysis = () => {
  const { deviceId } = useParams();
  // For single date or range selection
  const { deviceAnalysisData, alertData } = useSocket();
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [temperatureChartData, setTemperatureChartData] = useState([]);
  const [tdsChartData, setTdsChartData] = useState([]);
  const [batteryChartData, setBatteryChartData] = useState([]);
  const [deviceReadingData, setDeviceReadingData] = useState(null);
  // Get notifications from redux
  const deviceNotifications = useSelector(state => state.dashboard.deviceNotifications);
  const [deviceNotification, setDeviceNotification] = useState([]);
  const reduxDeviceAlerts = useSelector(state => state.dashboard.deviceAlerts);
  const [deviceAlerts, setDeviceAlerts] = useState(reduxDeviceAlerts || []);
  const [device, setDevice] = useState(null);
  const chartData = useSelector((state) => state.auth.chartData);
  const [modalOpen, setModalOpen] = useState(false);
  // Water level status color/label state
  const [tankStatusColor, setTankStatusColor] = useState('#6EA84D');
  const [tankStatusLabel, setTankStatusLabel] = useState('');
  const [tankCapacity, setTankCapacity] = useState('');
  const [currentWaterLtr, setCurrentWaterLtr] = useState(0);
  const [waterLevel, setWaterLevel] = useState(0);
  const location = useLocation();
  const userId = location.state?.userId;
  // Restrict datepicker to last 1 month window (today and one month back)
  // const _today = new Date();
  // const todayStart = new Date(_today.getFullYear(), _today.getMonth(), _today.getDate());
  // compute one month ago with safe handling for month lengths
  // const tentative = new Date(_today.getFullYear(), _today.getMonth() - 1, _today.getDate());
  // let oneMonthAgo;
  // if (tentative.getMonth() === (_today.getMonth() - 1 + 12) % 12) {
  //   oneMonthAgo = tentative;
  // } else {
  //   // previous month had fewer days (e.g., Mar 31 -> Feb 28). use last day of previous month
  //   oneMonthAgo = new Date(_today.getFullYear(), _today.getMonth(), 0);
  // }
    let todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  // exactly last 30 days (works across years)
  const oneMonthAgo = new Date(todayStart);
  oneMonthAgo.setDate(todayStart.getDate() - 30);

  // const chartDataLoading = useSelector((state) => state.auth.chartDataLoading);
  // const chartDataError = useSelector((state) => state.auth.chartDataError);
  const dispatch = useDispatch();
  // Fetch device notifications after device/user is loaded
  useEffect(() => {
    if (device && device.user && device.user.id) {

      dispatch(fetchDeviceNotifications(device.user.id));
    }
  }, [dispatch, device]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  useEffect(() => {
    setDeviceNotification(deviceNotifications?.notificationTypes);
  }, [deviceNotifications]);

  useEffect(() => {
    setDeviceAlerts(reduxDeviceAlerts?.data);
  }, [reduxDeviceAlerts]);

  useEffect(() => {
    // merge incoming alerts array into existing alerts array
    if (alertData?.data?.deviceId === Number(deviceId)) {
      const incoming = alertData?.data?.alerts;
      if (Array.isArray(incoming) && incoming.length) {
        setDeviceAlerts((prev) => {
          const prevArr = Array.isArray(prev) ? prev : [];
          return [...incoming, ...prevArr];
        });
      }
    }
  }, [alertData]);

  useEffect(() => {
    if (deviceId) {
      // Date logic for payload
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;

      let payload = { deviceId, userId };
      if (startDate && endDate) {
        // Both dates selected (range)
        payload.startDate = format(new Date(startDate), 'yyyy-MM-dd');
        payload.endDate = format(new Date(endDate), 'yyyy-MM-dd');
      } else if (startDate) {
        // Only one date selected
        payload.date = format(new Date(startDate), 'yyyy-MM-dd');
      } else {
        // No date selected
        payload.date = todayStr;
      }

      dispatch(fetchDeviceAnalysis({ deviceId, userId })).then((action) => {
        if (action.type.endsWith('fulfilled')) {
          setDevice(action.payload);
          // Set device name globally for AppLayout header
          // if (action.payload?.device?.name) {
          //   window.DEVICE_NAME = action.payload.device.name;
          // } else {
          //   window.DEVICE_NAME = '';
          // }
        }
      });

      dispatch(fetchDeviceChartData(payload));
      dispatch(fetchDeviceAlerts({ deviceId, userId })); // <-- fetch alerts for device
    }
  }, [deviceId, dispatch, startDate, endDate]);

  // Update tank status color/label when device.waterLevel changes
  useEffect(() => {
    if (device && typeof device.waterLevel !== 'undefined') {
      const wl = Number(device.waterLevel);
      const range = WATER_TANK_LEVEL_RANGES.find(r => wl >= r.min && wl <= r.max) || {};
      setTankStatusColor(range.color || '#6EA84D');
      setTankStatusLabel(range.label || '');
      const capacity = parseInt(device?.device?.deviceDimension?.capacity);
      setTankCapacity(capacity);
      setCurrentWaterLtr(parseInt((capacity * Number(device.waterLevel)) / 100));
      setWaterLevel(device.waterLevel);
    }
  }, [device]);

  useEffect(() => {
    if (chartData) {
      setTemperatureChartData(chartData?.deviceReadingData?.temperature || []);
      setTdsChartData(chartData?.deviceReadingData?.tds || []);
      setBatteryChartData(chartData?.deviceReadingData?.batteryLevel || []);
      setDeviceReadingData(chartData?.deviceReadingDetails || null);
    }

  }, [chartData]);

  //update device analysis data in real-time
  useEffect(() => {
    if (!deviceAnalysisData) return;

    // ensure incoming update is for current device
    if (Number(deviceAnalysisData?.data?.deviceId) !== Number(deviceId)) return;

    // normalize a date to midnight timestamp
    const normalize = (d) => {
      if (!d) return null;
      const dt = new Date(d);
      dt.setHours(0, 0, 0, 0);
      return dt.getTime();
    };

    const todayNorm = normalize(new Date());
    const startNorm = normalize(startDate);

    // allow realtime updates when:
    // - no date selected (default realtime), OR
    // - startDate is set and equals today (single-day view)
    const allowRealtime = (!startDate && !endDate) || (startNorm === todayNorm && !endDate);
    if (!allowRealtime) return;
    const stats = deviceAnalysisData?.data?.deviceStats || {};
    setTemperatureChartData(stats?.deviceReadingData?.temperature || []);
    setTdsChartData(stats?.deviceReadingData?.tds || []);
    setBatteryChartData(stats?.deviceReadingData?.batteryLevel || []);
    setDeviceReadingData(stats?.deviceReadingDetails || null);

    const wl = Number(stats?.deviceReadingDetails?.waterLevel ?? 0);
    const range = WATER_TANK_LEVEL_RANGES.find(r => wl >= r.min && wl <= r.max) || {};
    setTankStatusColor(range.color || '#6EA84D');
    setTankStatusLabel(range.label || '');

    const capacity = parseInt(stats?.device?.deviceDimension?.capacity ?? device?.device?.deviceDimension?.capacity, 10) || 0;
    setTankCapacity(capacity);
    setCurrentWaterLtr(Math.round((capacity * (wl || 0)) / 100));
    setWaterLevel(wl);
  }, [deviceAnalysisData, startDate, endDate, deviceId, device]);

  useEffect(() => {
    if (deviceAnalysisData) {
      dispatch(fetchDeviceAnalysis({ deviceId, userId })).then((action) => {
        if (action.type.endsWith('fulfilled')) {
          setDevice(action.payload);
        }
      });
    }
  }, [deviceAnalysisData]);

  // Tooltip formatters for each chart type
  const batteryTooltipFormatter = (params) => {
    if (!params || !params.length) return '';
    const p = params[0];
    return `<div>${p.axisValueLabel}<br/> solar battery : ${p.data} %</span></div>`;
  };

  const temperatureTooltipFormatter = (params) => {
    if (!params || !params.length) return '';
    const p = params[0];
    const temp = typeof p.data === 'number' ? p.data : 0;
    return `<div>${p.axisValueLabel}<br/> temperature : ${Math.round(temp)} °C</span></div>`;
  };

  const tdsTooltipFormatter = (params) => {
    if (!params || !params.length) return '';
    const p = params[0];
    const tds = typeof p.data === 'number' ? p.data : 0;
    return `<div>${p.axisValueLabel}<br/> TDS : ${tds} ppm</span></div>`;
  };

  // Allow only dates within the last 1 month window (inclusive)
  const filterDateByRange = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  // outside 1 month window
  if (d < oneMonthAgo || d > todayStart) return false;

  // no start date selected → allow
  if (!startDate) return true;

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  // allow max 7 days
  const maxEnd = new Date(start);
  maxEnd.setDate(start.getDate() + 6);

  // clamp max end to today
  const effectiveMax = maxEnd > todayStart ? todayStart : maxEnd;

  return d >= start && d <= effectiveMax;
};


  // if (!device) return <div>Loading...</div>;

  // Helper to map date strings to day names (Sun, Mon, etc.)
  const mapDatesToDayNames = (xLabels) => {
    if (!xLabels || !Array.isArray(xLabels)) return xLabels;
    return xLabels.map(dateStr => {
      // Try to parse as date, fallback to original if invalid
      try {
        return format(new Date(dateStr), 'EEE');
      } catch {
        return dateStr;
      }
    });
  };



  // If a date range is selected, transform xLabels to day names
  const isDateRange = !!(startDate && endDate);
  const batteryXLabels = isDateRange ? mapDatesToDayNames(batteryChartData?.xAxis) : batteryChartData?.xAxis;
  const temperatureXLabels = isDateRange ? mapDatesToDayNames(temperatureChartData?.xAxis) : temperatureChartData?.xAxis;
  const tdsXLabels = isDateRange ? mapDatesToDayNames(tdsChartData?.xAxis) : tdsChartData?.xAxis;


  const handleToggleSetting = idx => {
    setDeviceNotification(settings => settings.map((s, i) => i === idx ? { ...s, enabled: !s.enabled } : s));
  };

  return (
    <>
      <div className='device-analysis-wrapper'>
        <div className='device-info-section'>
          <div className='device-info-card'>
            <h2>Device Information <img src={settingIcon} alt="Settings" className="device-analysis-settings-icon" onClick={() => setModalOpen(true)} /></h2>
            <div className='details-row'>
              <div className='userDetails'>
                <h3>User Details</h3>
                <ul>
                  <li>{device?.user?.firstname + ' ' + device?.user?.lastname ?? ''}</li>
                  <li><img src={emailIcon} alt="Email" /> {device?.user?.email ?? ''}</li>
                  <li><img src={phoneIcon} alt="Phone" /> {device?.user?.phone ?? ''}</li>
                  <li><img src={locationIcon} alt="Location" /> {device?.user?.address ?? ''}</li>
                </ul>
              </div>
              <div className='deviceDetails'>
                <h3>Device Details</h3>
                <ul>
                  <li><strong>Status:</strong>
                    <span className={device?.device?.status === 'active' ? 'active' : 'inActive'}>{device?.device?.status === "active" ? 'Online' : 'Offline'}</span>
                  </li>
                  <li><strong>Install Date:</strong>
                    <span>{device?.device?.createdAt ? format(new Date(device?.device?.createdAt), 'yyyy-MM-dd') : '-'}</span>
                  </li>
                  <li><strong>Last Reading:</strong>
                    <span>{deviceReadingData?.updatedAt ? formatDistanceToNow(new Date(deviceReadingData?.updatedAt), { addSuffix: true }).replace(/^about\s+/, '') : '-'}</span>
                  </li>
                  <li><strong>Critical Alerts:</strong>
                    {/* <span className={device?.criticalAlerts === 'None' ? 'active' : 'inActive'}>{device?.criticalAlerts ?? 'None'}</span> */}
                    <span className={device?.criticalAlerts ? 'activeAlerts' : 'inActive'}>{device?.criticalAlerts ?? 'None'}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className='waterLevel-section'>
            <h2>Water level</h2>
            <div className="device-analysis-water-flex-row">
              {currentWaterLtr && device?.device?.status === "active" ? (
                <>
                  <div className="device-analysis-water-gauge-label">
                    <span className='tank-level-label'>
                      <img
                        src={(() => {
                          const wl = Number(waterLevel);
                          return wl >= 81 ? waterLevel100 :
                                 (wl >= 61 && wl <= 80) ? waterLevel75 :
                                 (wl >= 41 && wl <= 60) ? waterLevel50 :
                                 waterLevel25;
                        })()}
                        alt="Water Level"
                        style={{ marginRight: 0, verticalAlign: 'middle' }}
                      />
                      {currentWaterLtr ?? ''} ltr
                    </span>
                    <span className='tank-level-status' style={{
                      border: '2px solid ' + tankStatusColor,
                      color: tankStatusColor,
                    }}>
                      <span className="tank-level-status-dot" style={{
                        background: tankStatusColor,
                      }}></span>
                      <span className='text-nowrap'>
                        {tankStatusLabel}
                      </span>
                    </span>
                  </div>
                  <div className="device-analysis-water-gauge">
                    <WaterLevelGauge
                      value={waterLevel}
                      gaugeColor="#0077b6"
                      backgroundColor="#E6E8F7"
                      radius={70}
                      center={['50%', '70%']}
                      width={246}
                      height={246}
                      tankCapacity={tankCapacity}
                    />

                  </div>
                </>) : (<span style={{ width: '100%', textAlign: 'center', paddingTop: '16%', color: '#818d89' }}>No water level data available</span>)}
            </div>
          </div>
        </div>
        <div className='calendar-section'>
          <img src={calendarIcon} alt="calendar" />
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
            }}
    

            isClearable
            // className="device-analysis-date-input"
            calendarClassName="custom-calendar-dropdown"
            dayClassName={date => "custom-calendar-day"}
            dateFormat="yyyy-MM-dd"
            popperPlacement="bottom-start"
            showPopperArrow={false}
            placeholderText={format(todayStart, 'yyyy-MM-dd')}
            showMonthDropdown={false}
            showYearDropdown={false}
            renderCustomHeader={({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  changeMonth,
  changeYear,
}) => {
  // Only show current month and previous month (2 months max)
  const options = [];
  
  // Previous month
  const prevMonth = new Date(todayStart.getFullYear(), todayStart.getMonth() - 1, 1);
  options.push({
    year: prevMonth.getFullYear(),
    month: prevMonth.getMonth(),
    label: format(prevMonth, 'MMMM'),
  });
  
  // Current month (only if different from previous month)
  const currentMonth = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
  if (currentMonth.getTime() !== prevMonth.getTime()) {
    options.push({
      year: currentMonth.getFullYear(),
      month: currentMonth.getMonth(),
      label: format(currentMonth, 'MMMM'),
    });
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="react-datepicker__navigation react-datepicker__navigation--previous"
      >
        &lt;
      </button>

      {/* Month dropdown */}
      <select
        value={`${date.getFullYear()}-${date.getMonth()}`}
        onChange={(e) => {
          const [y, m] = e.target.value.split('-').map(Number);
          changeYear(y);
          changeMonth(m);
        }}
        style={{ border: 'none', background: 'transparent', fontWeight: 500 }}
      >
        {options.map(opt => (
          <option
            key={`${opt.year}-${opt.month}`}
            value={`${opt.year}-${opt.month}`}
          >
            {opt.label}
          </option>
        ))}
      </select>

      {/* Year label */}
      <span style={{ fontWeight: 500 }}>
        {date.getFullYear()}
      </span>

      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="react-datepicker__navigation react-datepicker__navigation--next"
      >
        &gt;
      </button>
    </div>
  );
}}

            filterDate={date => filterDateByRange(date)}
            minDate={oneMonthAgo}
            maxDate={todayStart}
            value={startDate && !endDate ? format(new Date(startDate), 'yyyy-MM-dd') : undefined}
          />
        </div>
      </div>
      <div className="device-analysis-content">
        <div className="device-analysis-charts-row">
          <div className="device-analysis-chart-card" style={{ minHeight: 400 }}>
            <div className="device-analysis-chart-title">Solar Battery</div>
            {(!batteryChartData || !Array.isArray(batteryChartData.value) || batteryChartData.value.length === 0 || batteryChartData.value.every(v => v === 0 || v === null || v === undefined)) ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#818d89', minHeight: 305 }}>No solar battery data available</div>
            ) : isDateRange ? (
              <BarChart
                data={batteryChartData.value}
                xLabels={batteryXLabels}
                barColor="#B2BCCB"
                height={305}
                tooltipFormatter={batteryTooltipFormatter}
              />
            ) : (
              <LineChart
                data={batteryChartData.value}
                xLabels={batteryXLabels}
                lineColor="#3D4744"
                height={305}
                tooltipFormatter={batteryTooltipFormatter}
              />
            )}
            {deviceReadingData && device?.device?.status === "active" &&
              <div className="device-analysis-chart-footer">
                {(() => {
                  const batteryLevelRaw = deviceReadingData?.batteryLevel ?? "";
                  const batteryLevel = Math.round(batteryLevelRaw);
                  const range = SOLAR_BATTERY_LEVEL_RANGES.find(r => batteryLevel >= r.min && batteryLevel <= r.max) || {};
                  return (

                    <>
                      <div className='chart-value-wrapper'>
                        <span className="device-analysis-chart-value">{`${batteryLevel} %`}</span>
                        <span className="device-analysis-chart-status-normal" style={{ color: range.color, border: `1px solid ${range.color}` || '#6EA84D' }}>

                          <span style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: range.color || '#D7184d',
                            verticalAlign: 'middle',
                          }}></span>
                          {range.label || 'Normal'}</span></div>
                      <span className="device-analysis-chart-desc">{range.impact || 'Battery is performing well with efficient charging cycles.'}</span>
                    </>
                  );
                })()}
              </div>
            }
          </div>
          <div className="device-analysis-chart-card" style={{ minHeight: 400 }}>
            <div className="device-analysis-chart-title">Temperature</div>
            {(!temperatureChartData || !Array.isArray(temperatureChartData.value) || temperatureChartData.value.length === 0 || temperatureChartData.value.every(v => v === 0 || v === null || v === undefined)) ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#818d89', minHeight: 305 }}>No temperature data available</div>
            ) : isDateRange ? (
              <BarChart
                data={temperatureChartData.value}
                xLabels={temperatureXLabels}
                barColor="#B2BCCB"
                height={305}
                tooltipFormatter={temperatureTooltipFormatter}
              />
            ) : (
              <LineChart
                data={temperatureChartData.value}
                xLabels={temperatureXLabels}
                lineColor="#3D4744"
                height={305}
                tooltipFormatter={temperatureTooltipFormatter}
              />
            )}
            {deviceReadingData && device?.device?.status === "active" &&
              <div className="device-analysis-chart-footer">
                {(() => {
                  const tempRaw = deviceReadingData?.temperature ?? 0;
                  const temp = Math.round(tempRaw);
                  const range = WATER_TEMPERATURE_RANGES.find(r => temp >= r.min && temp < r.max) || {};
                  return (
                    <>
                      <div className='chart-value-wrapper'>
                        <span className="device-analysis-chart-value">{`${temp} °C`}</span>
                        <span className="device-analysis-chart-status-high" style={{ color: range.color, border: `1px solid ${range.color}` || '#FFD530' }}>
                          <span style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: range.color || '#D7184d',
                            verticalAlign: 'middle',
                          }}></span>
                          {range.label || 'High'}</span></div>
                      <span className="device-analysis-chart-desc">{range.remarks || 'Temperature has exceeded the safe limit.'}</span>
                    </>
                  );
                })()}
              </div>
            }
          </div>
        </div>
        <div className="device-analysis-charts-row">
          <div className="device-analysis-chart-card-wide" style={{ minHeight: 400 }}>
            <div className="device-analysis-chart-title">TDS</div>
            {(!tdsChartData || !Array.isArray(tdsChartData.value) || tdsChartData.value.length === 0 || tdsChartData.value.every(v => v === 0 || v === null || v === undefined)) ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#818d89', minHeight: 305 }}>No TDS data available</div>
            ) : isDateRange ? (
              <BarChart
                data={tdsChartData.value}
                xLabels={tdsXLabels}
                barColor="#B2BCCB"
                height={305}
                tooltipFormatter={tdsTooltipFormatter}
              />
            ) : (
              <LineChart
                data={tdsChartData.value}
                xLabels={tdsXLabels}
                lineColor="#3D4744"
                height={305}
                tooltipFormatter={tdsTooltipFormatter}
              />
            )}
            {deviceReadingData && device?.device?.status === "active" && 
              <div className="device-analysis-chart-footer">
                {(() => {
                  const tdsRaw = deviceReadingData?.tds ?? 0;
                  const tds = Math.round(tdsRaw);
                  const range = TDS_RANGES.find(r => tds >= r.min && tds < r.max) || {};
                  return (
                    <>
                      <div className='chart-value-wrapper'>
                        <span className="device-analysis-chart-value">{`${tds} ppm`}</span>
                        <span className="device-analysis-chart-status-unsafe" style={{ color: range.color, border: `1px solid ${range.color}` || '#D7184d' }}>
                          <span style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: range.color || '#D7184d',
                            verticalAlign: 'middle',
                          }}></span>

                          {range.label || 'Unsafe'}</span></div>
                      <span className="device-analysis-chart-desc">{range.userText || 'TDS level is high. Water filtration recommended.'}</span>
                    </>
                  );
                })()}
              </div>
            }
          </div>
          <div className="dashboard-wrapper">
            <div className='recent-alerts'>
              <h2> Alerts</h2>
              <div className="dashboard-alerts-list" >
                {Array.isArray(deviceAlerts) && deviceAlerts.length > 0 ? (
                  deviceAlerts.map((alert, idx) => (
                    <div key={idx} className="dashboard-alert-item">
                      <div className='alert-dot' style={{ backgroundColor: alert?.color }}></div>
                      <div className="dashboard-alert-info">
                        {/* <div className="dashboard-alert-name">
                        {alert?.device?.name}
                      </div> */}
                        <div className="dashboard-alert-desc">
                          {alert.message} {alert.alertType === "TDS" ? <span>{"- "}{Math.round(alert?.severity)} {" ppm"}</span> : null}
                        </div>
                      </div>
                      <div className="dashboard-alert-meta">
                        <div className="dashboard-alert-time">
                          {getTimeAgo(alert.createdAt)}
                        </div>
                        <span
                          className={`dashboard-alert-icon-${alert.alertType}`}
                        >
                          <img
                            src={alertTriangleIcon}
                            alt="Alert"
                            style={{
                              filter: hexToCssFilter(`${alert.color}`),
                            }}
                          // style={{
                          //   filter: alert?.color
                          //     ? `brightness(0) saturate(100%) drop-shadow(0 0 0 ${alert.color})`
                          //     : 'none',
                          // }}
                          />
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: '#888',
                    }}
                  >
                    No recent alerts
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div >
      <DeviceNotificationSettingsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        notificationSettings={deviceNotification || []}
        onToggle={handleToggleSetting}
      />
    </>
  );
};

export default DeviceAnalysis;
